import { LabelPill, type LabelTone } from './LabelPill.js';

export interface RevenuePeriodPillProps {
  /** Pill text on the left side. E.g., "TOTAL", "EARNED". Wrapped in
   *  a LabelPill so it picks up the theme-aware token tinting. */
  label: string;
  /** Tone forwarded to LabelPill. Defaults to 'power' — matches every
   *  shipping consumer (HistoryPage's TOTAL, MyChargerEarningsPage's
   *  EARNED, LocationInfoCard's TOTAL). Available tones from
   *  @evi/ui's LabelPill: 'good' | 'power' | 'warn' | 'info' |
   *  'accent' | 'mute'. */
  tone?: LabelTone;
  /** Dollar amount on the right side. Renders as `$X.XX` with
   *  tabular-nums for column alignment when two pills sit side-by-
   *  side. */
  amount: number;
  /** Session count for the period. Drives the singular/plural copy
   *  on "SESSION(S)". */
  sessionCount: number;
  /** Formats `amount` (dollars, a float). Default "$X.XX". A surface
   *  that knows its currency passes one (the PWA Earnings page prints
   *  CA$ for a Canadian home, item 3). */
  formatAmount?: (amount: number) => string;
  /** Forwarded for consumer-side margin overrides if the chart's
   *  footer slot wants tighter / looser spacing than the default. */
  className?: string;
}

/**
 * Shared "period total" pill for revenue-chart footers. Three near-
 * identical inline implementations preceded this:
 *   - PeriodTotalPill   on PWA HistoryPage             (label "TOTAL")
 *   - OwnerEarningsPill on PWA MyChargerEarningsPage   (label "EARNED")
 *   - LocationPeriodPill on admin LocationInfoCard     (label "TOTAL")
 *
 * They all rendered: `<LabelPill> $X · N SESSION(S)`, identical
 * tabular-nums + singular/plural copy + colour tokens. Three sites
 * crossed the "copy first, promote on third consumer" threshold
 * from the BottomSheet precedent (already in @evi/ui), so this lift
 * is the consolidation.
 *
 * Markup is the verbatim shape the three originals shipped with —
 * tone parameterised on the label, value colour stays
 * `var(--evi-power)` (the power token, brand green / luce amber per
 * theme) because every existing consumer uses that for the dollar
 * value regardless of label tone. If a future consumer wants a
 * differently-coloured value, add a `valueTone` prop then.
 */
export function RevenuePeriodPill({
  label,
  tone = 'power',
  amount,
  sessionCount,
  formatAmount,
  className,
}: RevenuePeriodPillProps) {
  return (
    <span
      className={className}
      style={{
        display: 'contents',
      }}
    >
      <LabelPill tone={tone}>{label}</LabelPill>
      <span>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 500,
            color: 'var(--evi-power)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatAmount ? formatAmount(amount) : `$${amount.toFixed(2)}`}
        </span>
        <span
          style={{
            fontSize: '10px',
            color: 'var(--evi-mute)',
            marginLeft: '6px',
            letterSpacing: '1px',
          }}
        >
          · {sessionCount} {sessionCount === 1 ? 'SESSION' : 'SESSIONS'}
        </span>
      </span>
    </span>
  );
}
